import type { Material } from '@babylonjs/core/Materials/material';
import type { AssetLoader } from '../assets/AssetLoader';
import type { FlashingMaterialConfig } from '../config/rendering.config';

/** Brightness 0..1 of a flashing lamp at `time` (seconds): lit for `duty` of every period, else `floor`. */
export function flashLevel(time: number, hz: number, duty: number, floor: number): number {
  const phase = (((time * hz) % 1) + 1) % 1;
  return phase < duty ? 1 : floor;
}

interface EmissiveMaterial extends Material {
  emissiveIntensity: number;
}

interface Target {
  readonly material: EmissiveMaterial;
  readonly base: number;
}

/**
 * Flashes emissive materials of decoration assets (ENV_WARNING_LIGHT_A: the lens is its own mesh
 * and material). Instances share the asset's material, so every lamp of a type flashes in step –
 * like a chain of site warning lights. Assets that are not loaded (greybox fallback) stay as they are.
 */
export class MaterialFlasher {
  private readonly targets = new Map<FlashingMaterialConfig, Target | null>();

  constructor(
    private readonly loader: AssetLoader,
    private readonly configs: readonly FlashingMaterialConfig[],
  ) {}

  update(time: number): void {
    for (const config of this.configs) {
      const target = this.resolve(config);
      if (target) target.material.emissiveIntensity = target.base * flashLevel(time, config.hz, config.duty, config.floor);
    }
  }

  private resolve(config: FlashingMaterialConfig): Target | null {
    const known = this.targets.get(config);
    if (known !== undefined) return known;
    const container = this.loader.get(config.assetId);
    if (!container) return null; // not loaded yet – look again next frame
    const material = container.materials.find((m) => m.name === config.material);
    const target = material && isEmissive(material) ? { material, base: material.emissiveIntensity } : null;
    if (!target) console.warn(`[rendering] ${config.assetId}: no emissive material ${config.material} to flash`);
    this.targets.set(config, target);
    return target;
  }
}

function isEmissive(material: Material): material is EmissiveMaterial {
  return 'emissiveIntensity' in material && typeof material.emissiveIntensity === 'number';
}
